import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllConferences } from '../../services/api';

const Conferences = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Add CSS for responsive images in descriptions
    const style = document.createElement('style');
    style.textContent = `
      .card-text img {
        max-width: 100% !important;
        height: auto !important;
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Cleanup: remove the style when component unmounts
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const loadConferences = async () => {
      try {
        setLoading(true);
        const data = await fetchAllConferences();
        
        // Sort conferences by start date (most recent first)
        const sortedConferences = data.sort((a, b) => {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setConferences(sortedConferences);
      } catch (err) {
        console.error('Error fetching conferences:', err);
        setError('Failed to load conferences. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadConferences();
  }, []);

  const LOCALE = 'en-GB';
  const baseUrl = process.env.REACT_APP_API_URL || '/api';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Etc/GMT'
    });
  };

  const getConferenceStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      return { status: 'upcoming', class: 'badge bg-primary', text: 'Upcoming' };
    } else if (now >= start && now <= end) {
      return { status: 'ongoing', class: 'badge bg-success', text: 'Ongoing' };
    } else {
      return { status: 'past', class: 'badge bg-secondary', text: 'Past' };
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-conference-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading conferences...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="display-6 fw-bold text-conference-primary">
                <i className="bi bi-calendar-event me-3"></i>
                MFS Conferences
              </h1>
              <p className="lead text-muted">
                Explore our past and upcoming academic conferences
              </p>
            </div>
          </div>

          {conferences.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-calendar-x display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No Conferences Available</h3>
              <p className="text-muted">
                There are currently no conferences to display.
              </p>
            </div>
          ) : (
            <div className="row">
              {conferences.map((conference) => {
                const statusInfo = getConferenceStatus(conference.startDate, conference.endDate);
                
                return (
                  <div key={conference._id} className="col-lg-6 col-xl-4 mb-4">
                    <div className="card h-100 shadow-sm border-0 card-conference">
                      <div className="card-header bg-conference-gradient text-white">
                        <div className="d-flex justify-content-between align-items-start">
                          <h5 className="card-title mb-0 text-white">
                            {conference.title}
                          </h5>
                          <span className={statusInfo.class}>
                            {statusInfo.text}
                          </span>
                        </div>
                      </div>
                      
                      <div className="card-body d-flex flex-column">
                        {conference.description && (
                          <div 
                            className="card-text mb-3"
                            dangerouslySetInnerHTML={{
                              __html: conference.description
                            }}
                            style={{
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}
                          />
                        )}
                        
                        <div className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-calendar-check me-2 text-conference-primary"></i>
                            <small>
                              <strong>Start: {formatDate(conference.startDate)} </strong>
                            </small>
                          </div>
                          
                          {conference.endDate && (
                            <div className="d-flex align-items-center mb-2">
                              <i className="bi bi-calendar-x me-2 text-conference-primary"></i>
                              <small>
                                <strong>End: {formatDate(conference.endDate)} </strong>
                              </small>
                            </div>
                          )}
                          
                          {conference.location && (
                            <div className="d-flex align-items-center mb-2">
                              <i className="bi bi-geo-alt me-2 text-conference-primary"></i>
                              <small>
                                <strong>Location: {conference.location} </strong>
                              </small>
                            </div>
                          )}

                          {(() => {
                            const booklet = conference?.files?.find(f => f.isBooklet);
                            if (!booklet) return null;
                            return (
                              <a
                                href={`${baseUrl}/conferences/files/${booklet.filename}`}
                                download={booklet.originalName || booklet.filename}
                                className="btn btn-outline-primary btn-sm w-100"
                                title="Download brochure"
                              >
                                <i className="bi bi-journal-arrow-down me-2"></i>
                                Brochure
                              </a>
                            );
                          })()}
                        </div>

                        <div className="mt-auto d-grid gap-2">
                          <Link 
                            to={`/conference/${conference._id}`} 
                            className="btn btn-conference-primary btn-sm"
                          >
                            <i className="bi bi-eye me-2"></i>
                            View Details
                          </Link>

                          
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conferences;
